import { sortMembersByType, teamsClient } from '@atlaskit/teams-client';
import { isResultWithPageInfo, type TeamMembership } from '@atlaskit/teams-client/types';

import { logException } from '../../../common/utils/sentry';
import type { StoreApi } from '../types';

// This must be dispatched AFTER team profile, team members and my team
// membership have already been resolved.
export const getRequestingToJoinUsers =
	(teamId: string) =>
	async ({ getState, setState }: StoreApi): Promise<TeamMembership[]> => {
		const initialState = getState();

		const isOpenTeam = initialState.teamProfile.data.membershipSettings === 'OPEN';
		const membershipErrors = initialState.teamMembers.hasError;
		const hasPermissionToViewRequestingUsers =
			initialState.teamProfile.data.permission === 'FULL_WRITE';
		const teamChanged = initialState.teamMembers.teamId !== teamId;

		if (teamChanged || !hasPermissionToViewRequestingUsers || membershipErrors || isOpenTeam) {
			return [];
		}

		try {
			const requestingUsersResponse = await teamsClient.queryTeamMemberships(
				teamId,
				['REQUESTING_TO_JOIN'],
				{
					first: 100,
					after: initialState.teamMembers.requestingToJoinUsersPageInfo.cursor ?? undefined,
				},
			);

			const requestingUsers = isResultWithPageInfo<TeamMembership>(requestingUsersResponse)
				? requestingUsersResponse.edges
				: requestingUsersResponse;

			const state = getState();

			if (state.teamMembers.teamId !== teamId) {
				return [];
			}

			const sortedRequesters = sortMembersByType(requestingUsers);

			setState({
				teamMembers: {
					...state.teamMembers,
					requestingToJoinUsers: sortedRequesters,
				},
			});

			return sortedRequesters;
		} catch (err) {
			logException(err, 'Could not load requesting-to-join users');
			return [];
		}
	};

export const declineJoinRequest =
	(teamId: string, memberId: string) =>
	async ({ getState, setState }: StoreApi) => {
		await teamsClient.declineJoinRequest(teamId, memberId);

		const state = getState();

		if (state.teamMembers.teamId !== teamId) {
			return;
		}

		setState({
			...state,
			teamMembers: {
				...state.teamMembers,
				requestingToJoinUsers: state.teamMembers.requestingToJoinUsers.filter(
					(user) => user.membershipId.memberId !== memberId,
				),
			},
		});
	};
